import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";

import CustomButton from "components/common/Button";
import CustomSnackbar, { AlertType } from "components/common/Snackbar";
import downloadFile from "util/downloadHelper";
import InlineLoader from "./InlineLoader";
import {
  listBillingChargeApprovals,
  exportBillingChargeApprovalsCsv,
  bulkApproveBillingChargeApprovals,
} from "services/billing";
import type { BsPaginated } from "services/billing";

import ChargeApprovalDetailModal from "./ChargeApprovalDetailModal";
import { useCursorList } from "./useCursorList";
import { CustomChip, DatePicker, TableComponent } from "./ui";
import { BsChargeApproval, formatDate, formatMoney, statusColor, titleCase } from "./types";

const STATUS_FILTERS = ["pending", "approved", "rejected"] as const;

/** Backend errors arrive as the `error[0]?.description` array shape used app-wide. */
const errorMessage = (err: unknown): string => {
  const description = (err as Array<{ description?: string }> | undefined)?.[0]?.description;
  return description || "Something went wrong, please try again";
};

// <input type="date"> yields a Date at local midnight; read its Y/M/D directly
// rather than toISOString(), which would shift across a UTC day boundary.
const toIsoDateOnly = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const columns = [
  {
    id: "customerName",
    label: "Customer",
    render: (v: string | null, row: BsChargeApproval) => v || row.customerMonolithId || "—",
  },
  {
    id: "status",
    label: "Status",
    render: (v: string) => <CustomChip label={titleCase(v)} size="small" color={statusColor(v)} />,
  },
  { id: "amountCents", label: "Amount", render: (v: number, row: BsChargeApproval) => formatMoney(v, row.currency) },
  { id: "createdAt", label: "Queued", render: (v: string) => formatDate(v) },
  { id: "reviewedBy", label: "Reviewed by", render: (v: string | null) => v || "—" },
];

/**
 * [PRD-3321] Manual charge approval gate — queue of recurring charges held
 * back while bs_config.auto_charge_enabled is false, pending admin review.
 */
export default function ApprovalsTab() {
  const [status, setStatus] = useState<(typeof STATUS_FILTERS)[number]>("pending");
  const [bulkDate, setBulkDate] = useState<Date | null>(null);
  // Date filtering only applies to the pending backlog — Approved/Rejected
  // always show their full history regardless of whatever's in the picker.
  const dateFilter = status === "pending" && bulkDate ? toIsoDateOnly(bulkDate) : undefined;
  const load = useCallback(
    (cursor?: string) =>
      listBillingChargeApprovals({ status, cursor, limit: 25, date: dateFilter }) as Promise<
        BsPaginated<BsChargeApproval>
      >,
    [status, dateFilter]
  );
  const { rows, hasMore, loading, loadingMore, error, loadMore, reload } = useCursorList<BsChargeApproval>(load);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; type: AlertType }>({
    open: false,
    message: "",
    type: AlertType.success,
  });
  const [exporting, setExporting] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{
    approvedCount: number;
    skippedGuardCount: number;
    failedCount: number;
  } | null>(null);
  // A broader, unfiltered snapshot of the pending backlog — used only to tell
  // the admin whether other days still need attention once the current day's
  // view empties out, so clearing one day doesn't look like "nothing left."
  const [pendingSummary, setPendingSummary] = useState<BsChargeApproval[] | null>(null);
  const loadPendingSummary = useCallback(async () => {
    try {
      const page = (await listBillingChargeApprovals({
        status: "pending",
        limit: 200,
      })) as BsPaginated<BsChargeApproval>;
      setPendingSummary(page?.data ?? []);
    } catch {
      setPendingSummary(null);
    }
  }, []);

  // Rows load newest-first, so the last loaded row is the oldest. Default the
  // picker to it (not "today") — a backlog reviewed a day late must point at
  // whichever day actually has pending rows, not whatever day it is right now.
  useEffect(() => {
    if (status === "pending" && bulkDate === null && rows.length > 0) {
      setBulkDate(new Date((rows[rows.length - 1] as BsChargeApproval).createdAt));
    }
  }, [status, rows, bulkDate]);

  useEffect(() => {
    if (status === "pending") {
      loadPendingSummary();
    }
  }, [status, loadPendingSummary]);

  const otherDaysPendingCount =
    dateFilter && pendingSummary
      ? pendingSummary.filter((a) => toIsoDateOnly(new Date(a.createdAt)) !== dateFilter).length
      : 0;

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const blob = await exportBillingChargeApprovalsCsv();
      downloadFile(blob, `charge-approvals-pending-${toIsoDateOnly(new Date())}.csv`);
    } catch (err) {
      setSnackbar({ open: true, message: errorMessage(err), type: AlertType.error });
    } finally {
      setExporting(false);
    }
  };

  const handleBulkApprove = async () => {
    if (!bulkDate || bulkSubmitting) return;
    setBulkSubmitting(true);
    const dateStr = toIsoDateOnly(bulkDate);
    let cursor: string | undefined;
    let approvedCount = 0;
    let skippedGuardCount = 0;
    const failed: { id: string; reason: string }[] = [];
    setBulkProgress({ approvedCount: 0, skippedGuardCount: 0, failedCount: 0 });

    try {
      // Walk every page: a production day can hold far more rows than fit in
      // one request, so this chunks through instead of doing it all at once.
      let hasMore = true;
      while (hasMore) {
        // eslint-disable-next-line no-await-in-loop
        const result = await bulkApproveBillingChargeApprovals(dateStr, cursor);
        approvedCount += result.approvedCount;
        skippedGuardCount += result.skippedGuardCount;
        failed.push(...result.failed);
        setBulkProgress({ approvedCount, skippedGuardCount, failedCount: failed.length });
        hasMore = result.hasMore;
        cursor = result.cursor ?? undefined;
      }

      setBulkConfirmOpen(false);
      const parts = [`Approved ${approvedCount}`, `skipped ${skippedGuardCount} (guard warning)`];
      if (failed.length > 0) parts.push(`${failed.length} failed`);
      setSnackbar({
        open: true,
        message: `${parts.join(", ")} for ${dateStr}.`,
        type: failed.length > 0 ? AlertType.error : AlertType.success,
      });
      reload();
      loadPendingSummary();
    } catch (err) {
      setSnackbar({
        open: true,
        message: `${errorMessage(err)} (partial progress: approved ${approvedCount}, skipped ${skippedGuardCount}, failed ${failed.length})`,
        type: AlertType.error,
      });
      reload();
    } finally {
      setBulkSubmitting(false);
      setBulkProgress(null);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-05 justify-between items-center flex-wrap">
        <div className="flex gap-05">
          {STATUS_FILTERS.map((s) => (
            <CustomChip
              key={s}
              label={titleCase(s)}
              size="small"
              onClick={() => setStatus(s)}
              variant={status === s ? "filled" : "outlined"}
              color={status === s ? "primary" : "default"}
            />
          ))}
        </div>

        <div className="flex gap-05 items-end">
          {status === "pending" && (
            <>
              <CustomButton
                label={exporting ? "Exporting…" : "Export CSV"}
                onClick={handleExportCsv}
                isDisabled={exporting || rows.length === 0}
              />
              <div style={{ width: 165 }}>
                <DatePicker
                  name="bulk-approve-date"
                  label=""
                  value={bulkDate}
                  onChange={setBulkDate}
                  placeholder="Day"
                  maxDate={new Date()}
                />
              </div>
              <CustomButton
                label="Bulk approve day"
                onClick={() => setBulkConfirmOpen(true)}
                isDisabled={!bulkDate || rows.length === 0}
              />
            </>
          )}
        </div>
      </div>

      {otherDaysPendingCount > 0 && (
        <p className="para-body-s-regular color-text-body-secondary">
          {otherDaysPendingCount} more pending on other days — change the date above to review them.
        </p>
      )}

      {loading ? (
        <InlineLoader />
      ) : error ? (
        <p className="para-body-m-regular color-text-body-secondary">{error}</p>
      ) : (
        <>
          <TableComponent
            columns={columns}
            data={rows}
            disableSort
            onRowClick={(row: BsChargeApproval) => setSelectedId(row.id)}
          />
          {hasMore && (
            <div className="flex justify-center mt-05">
              <CustomButton label={loadingMore ? "Loading…" : "Load more"} onClick={loadMore} isDisabled={loadingMore} />
            </div>
          )}
        </>
      )}

      {selectedId && (
        <ChargeApprovalDetailModal
          approvalId={selectedId}
          onClose={() => setSelectedId(null)}
          onSuccess={(message) => {
            setSnackbar({ open: true, message, type: AlertType.success });
            reload();
          }}
          onError={(message) => setSnackbar({ open: true, message, type: AlertType.error })}
        />
      )}

      {bulkConfirmOpen && bulkDate && (
        <Dialog open onClose={() => setBulkConfirmOpen(false)}>
          <DialogTitle>Bulk approve {toIsoDateOnly(bulkDate)}?</DialogTitle>
          <DialogContent>
            <p className="para-body-m-regular color-text-body-secondary">
              This approves every still-pending charge queued on {toIsoDateOnly(bulkDate)} and charges each
              customer&apos;s payment method. Any charge with an active recurring-charge guard warning is skipped
              and left pending for individual review.
            </p>
            {bulkProgress && (
              <p className="para-body-s-regular color-text-body-secondary mt-05">
                Approved {bulkProgress.approvedCount}, skipped {bulkProgress.skippedGuardCount}, failed{" "}
                {bulkProgress.failedCount} so far…
              </p>
            )}
          </DialogContent>
          <DialogActions>
            <CustomButton label="Cancel" onClick={() => setBulkConfirmOpen(false)} isDisabled={bulkSubmitting} />
            <CustomButton
              label={bulkSubmitting ? "Approving…" : "Confirm bulk approve"}
              onClick={handleBulkApprove}
              isDisabled={bulkSubmitting}
            />
          </DialogActions>
        </Dialog>
      )}

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        type={snackbar.type}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
}
